import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  LinearProgress,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  RadioButtonChecked,
  Circle,
  Star,
  CheckCircle,
  Brightness5,
  Person,
} from "@mui/icons-material";
import { observer } from "mobx-react";
import styled from "styled-components";
import { RequestState } from "../../modules/constants";

const GoalCard = styled(Card)`
  height: 120px;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const NutrientIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${(props) => props.color || "#f5f5f5"};
  margin-right: 12px;
`;

const ProgressContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const getProgressColor = (percentage) => {
  if (percentage >= 100) return "#4caf50";
  if (percentage >= 75) return "#8bc34a";
  if (percentage >= 50) return "#ff9800";
  return "#f44336";
};

const calculatePercentage = (consumed, target) => {
  if (target === 0) return 0;
  return Math.min((consumed / target) * 100, 100);
};

export const DietaryGoalsWidget = observer(({ controller }) => {
  const { getDietaryGoals, getDietaryGoalsRequestState } = controller;
  const isLoading = getDietaryGoalsRequestState === RequestState.REQUESTED;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const nutrients = [
    {
      key: "calories",
      label: "Calories",
      icon: <RadioButtonChecked sx={{ color: "white", fontSize: 20 }} />,
      consumed: getDietaryGoals.caloriesConsumed,
      target: getDietaryGoals.caloriesTarget,
      unit: "kcal",
      color: "#ff5722",
    },
    {
      key: "protein",
      label: "Protein",
      icon: <Circle sx={{ color: "white", fontSize: 20 }} />,
      consumed: getDietaryGoals.proteinConsumed,
      target: getDietaryGoals.proteinTarget,
      unit: "g",
      color: "#e91e63",
    },
    {
      key: "carbs",
      label: "Carbs",
      icon: <Star sx={{ color: "white", fontSize: 20 }} />,
      consumed: getDietaryGoals.carbsConsumed,
      target: getDietaryGoals.carbsTarget,
      unit: "g",
      color: "#ffc107",
    },
    {
      key: "fat",
      label: "Fat",
      icon: <CheckCircle sx={{ color: "white", fontSize: 20 }} />,
      consumed: getDietaryGoals.fatConsumed,
      target: getDietaryGoals.fatTarget,
      unit: "g",
      color: "#ff9800",
    },
    {
      key: "fiber",
      label: "Fiber",
      icon: <Brightness5 sx={{ color: "white", fontSize: 20 }} />,
      consumed: getDietaryGoals.fiberConsumed,
      target: getDietaryGoals.fiberTarget,
      unit: "g",
      color: "#4caf50",
    },
    {
      key: "water",
      label: "Water",
      icon: <Person sx={{ color: "white", fontSize: 20 }} />,
      consumed: getDietaryGoals.waterConsumed,
      target: getDietaryGoals.waterTarget,
      unit: "cups",
      color: "#2196f3",
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Dietary Goals Progress"
        subheader="Track your daily nutritional intake"
      />
      <CardContent>
        <Grid container spacing={2}>
          {nutrients.map((nutrient) => {
            const percentage = calculatePercentage(
              nutrient.consumed,
              nutrient.target
            );
            const progressColor = getProgressColor(percentage);

            return (
              <Grid item xs={12} sm={6} md={4} key={nutrient.key}>
                <GoalCard>
                  <CardContent>
                    <Box display="flex" alignItems="center">
                      <NutrientIcon color={nutrient.color}>
                        {nutrient.icon}
                      </NutrientIcon>
                      <Box flex={1}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {nutrient.label}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {nutrient.consumed.toLocaleString()} /{" "}
                          {nutrient.target.toLocaleString()} {nutrient.unit}
                        </Typography>
                        <ProgressContainer>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              flex: 1,
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: "#f0f0f0",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: progressColor,
                                borderRadius: 4,
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ minWidth: 35, fontWeight: "bold" }}
                          >
                            {percentage.toFixed(0)}%
                          </Typography>
                        </ProgressContainer>
                      </Box>
                    </Box>
                  </CardContent>
                </GoalCard>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
});

export default DietaryGoalsWidget;
